import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

const benefits = [
  {
    kicker: 'Benefit 01',
    title: 'Acts as a Digital Product Expert',
    description:
      'Help visitors navigate nuanced decisions around amplifiers, DACs, speakers, streaming, and room fit with guidance that feels consultative instead of robotic.',
  },
  {
    kicker: 'Benefit 02',
    title: 'Increases Conversions & Order Value',
    description:
      'Recommend full-system pairings and complementary products in real time, helping more buyers move forward while naturally opening higher-value builds.',
  },
  {
    kicker: 'Benefit 03',
    title: 'Answers Technical Questions Instantly',
    description:
      'Keep shoppers on your site when they ask things like compatibility, speaker load, streaming support, cable needs, and setup considerations.',
  },
  {
    kicker: 'Benefit 04',
    title: 'Delivers 24/7 Global Support',
    description:
      'Premium buyers browse across time zones and after business hours. The assistant is always available, without making prospects wait for a reply.',
  },
  {
    kicker: 'Benefit 05',
    title: 'Generates Qualified Dealer Inquiries',
    description:
      'Capture high-intent visitors, understand their system goals, and route stronger, better-qualified interest to your dealer network or internal sales team.',
  },
  {
    kicker: 'Benefit 06',
    title: 'Protects the Premium Brand Experience',
    description:
      'The goal is not replacement. It is a refined digital extension of your expertise, preserving the personalized feel that high-end audio customers expect.',
  },
];

const benefitsStrip = [
  { value: '24/7', label: 'brand-aligned product guidance' },
  { value: 'Instant', label: 'answers to technical buying questions' },
  { value: 'Higher', label: 'system-value recommendation potential' },
  { value: 'Premium', label: 'consultative digital buying experience' },
];

const challenges = [
  {
    title: 'Complex Decisions Without Showroom Guidance',
    description:
      'High-end audio purchases involve amplification, speakers, room size, source components, and personal preferences. Most websites present specs but do not guide the full decision.',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 8h10M7 12h6m-6 4h8M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    ),
  },
  {
    title: 'High-Intent Visitors Leave to Research Elsewhere',
    description:
      'When a buyer cannot get immediate answers around compatibility, streaming support, or room fit, they leave your site for reviews, forums, dealers, or competitors.',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 9l4-4 4 4m0 6l-4 4-4-4"
      />
    ),
  },
  {
    title: 'Sales & Dealer Teams Cannot Be Instantly Available',
    description:
      'Luxury buyers browse globally and often after hours. Delayed follow-up creates drop off, weaker engagement, and missed opportunities to capture qualified interest.',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
  },
];

const solutions = [
  {
    title: 'Digital Product Expertise',
    description:
      'Guide visitors the way a knowledgeable specialist would, based on room size, listening habits, budget, and existing gear.',
    bullets: [
      'Room-size and layout guidance',
      'Listening-profile discovery',
      'Existing gear and upgrade-path support',
    ],
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6v6l4 2m6 0A10 10 0 1112 2a10 10 0 0110 10z"
      />
    ),
  },
  {
    title: 'Intelligent System Recommendations',
    description:
      'Recommend complete systems, not isolated products, so buyers understand how speakers, amplification, streaming, and accessories work together.',
    bullets: [
      'Amplifier and speaker matching',
      'Streaming, DAC, and source recommendations',
      'Complementary add-ons for fuller system builds',
    ],
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11H5m14 0a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    ),
  },
  {
    title: 'Instant Technical Question Handling',
    description:
      'Keep visitors on your site by answering the questions that usually turn into long email chains or forum searches.',
    bullets: [
      'Speaker load, power, and compatibility questions',
      'Streaming ecosystem and platform support',
      'Setup, cables, and room-consideration guidance',
    ],
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
  },
  {
    title: 'Dealer-Ready Leads & Qualified Interest',
    description:
      'Turn passive browsing into meaningful conversations by capturing intent and routing better-informed prospects to the right dealer or sales team.',
    bullets: [
      'Demo and dealer inquiry capture',
      'Pre-qualified system interests and budgets',
      'Consistent premium messaging across regions',
    ],
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 8h2a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-8a2 2 0 012-2h2m10 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m10 0H7"
      />
    ),
  },
];

const HighFidelityAudioHomeTheaterUseCase = () => {
  return (
    <div>
      <NextSeo
        title="High-Fidelity Audio & Home Theater AI Solutions"
        description="Turn your website into a digital product specialist for high-end audio and home theater buyers. Guide system matching, answer technical questions, and generate dealer-ready leads with Chat.co."
        canonical="https://chat.co/use-cases/high-fidelity-audio-home-theater"
      />

      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cyan-50 to-white px-4 pb-[82px] pt-[92px] sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-14">
            <div className="flex flex-col justify-center">
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-teal-100 px-4 py-2 text-sm font-medium text-teal-700">
                <span className="h-2.5 w-2.5 rounded-full bg-teal-500 shadow-[0_0_0_5px_rgba(20,184,166,0.14)]" />
                High-Fidelity Audio &amp; Home Theater
              </div>
              <h1
                className="mt-[22px] max-w-[10ch] text-[clamp(2.95rem,5vw,4.9rem)] font-extrabold leading-[1.04] tracking-[-0.05em] text-gray-900"
                style={{ textWrap: 'balance' }}>
                Guide Premium Buyers with AI-Powered Audio Expertise
              </h1>
              <p className="mt-6 max-w-[60ch] text-[1.12rem] leading-[1.6] text-gray-600">
                In the high-fidelity audio space, your customers are not just buying products. They
                are investing in an experience. Chat.co helps your brand bring showroom-quality
                guidance online with instant support around system matching, room considerations,
                product comparisons, and full-system recommendations.
              </p>
              <div className="mt-[34px] flex flex-col gap-[14px] sm:flex-row sm:items-center sm:gap-[14px]">
                <Link
                  href={PageLink.contactUs}
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-primary-600 px-8 py-3 text-base font-semibold text-white hover:bg-primary-700">
                  Schedule Demo
                </Link>
                <Link
                  href={PageLink.login}
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full border border-gray-300 bg-white px-8 py-3 text-base font-semibold text-gray-700 hover:bg-gray-50">
                  Start Free Trial
                </Link>
              </div>
            </div>

            <div className="relative h-[400px] lg:h-[500px]">
              <Image
                src="https://images.unsplash.com/photo-1750516028146-379e8095001e?auto=format&fit=crop&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800"
                alt="High-end audio speaker system in a premium listening environment"
                fill
                className="rounded-3xl object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="border-y border-gray-200/70 bg-gradient-to-b from-amber-50/40 to-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              What This Unlocks for Premium Audio Brands
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Transform your website from a static catalog into a guided, immersive buying journey
              that feels closer to a premium showroom experience.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {benefits.map((benefit) => (
              <article
                key={benefit.title}
                className="rounded-2xl border border-gray-200 bg-white/95 p-7 shadow-[0_14px_34px_rgba(10,18,30,0.08)]">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-amber-700">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  {benefit.kicker}
                </div>
                <h3 className="mt-4 text-xl font-semibold text-gray-900">{benefit.title}</h3>
                <p className="mt-3 text-base leading-7 text-gray-600">{benefit.description}</p>
              </article>
            ))}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {benefitsStrip.map((item) => (
              <div
                key={item.value}
                className="rounded-2xl border border-gray-200 bg-white px-5 py-5 text-center">
                <strong className="block text-lg font-semibold tracking-tight text-gray-900">
                  {item.value}
                </strong>
                <span className="mt-2 block text-sm text-gray-500">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              The Challenge in High-End Audio &amp; Home Theater
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
              Premium buyers do deep research, ask nuanced technical questions, and expect expert
              guidance before they commit to a purchase.
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {challenges.map((challenge) => (
              <article
                key={challenge.title}
                className="rounded-2xl border border-red-100 bg-red-50 p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    {challenge.icon}
                  </svg>
                </div>
                <h3 className="mt-4 text-xl font-semibold text-gray-900">{challenge.title}</h3>
                <p className="mt-2 text-sm leading-7 text-gray-600">{challenge.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              How Chat.co Transforms the Buying Journey
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
              A digital product expert trained on your brand, your catalog, and your technical
              knowledge base.
            </p>
          </div>

          <div className="mt-16 grid gap-12 lg:grid-cols-2">
            {solutions.map((solution) => (
              <div key={solution.title} className="flex gap-6">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100">
                  <svg
                    className="h-6 w-6 text-amber-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    {solution.icon}
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{solution.title}</h3>
                  <p className="mt-2 text-base leading-7 text-gray-600">{solution.description}</p>
                  <ul className="mt-4 space-y-2">
                    {solution.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-sm text-gray-600">
                        <svg
                          className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Ready to Modernize the Premium Buying Experience?
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Show buyers how an AI-powered assistant can support system discovery, answer technical
            questions instantly, and turn your site into a more consultative sales experience.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              href={PageLink.demo}
              className="inline-flex items-center justify-center rounded-full bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700">
              Schedule a Demo
            </Link>
            <Link
              href={PageLink.login}
              className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-8 py-3 text-base font-medium text-gray-700 hover:bg-gray-50">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default HighFidelityAudioHomeTheaterUseCase;
